import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ViewContainerRef, ComponentFactoryResolver, Type } from '@angular/core';
import { IDialogView } from '@shared/components/ok-cancel-dialog/ok-cancel-dialog.component';
import { DialogModel } from '@shared/models/dialog.model';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { GetSmartGridFieldDataTypesAction } from '@shared/store/actions/smart-grid.actions';
import { getSmartGridFieldDataTypes } from '@shared/store/selectors/smart-grid.selectors';
import { ParticleState } from '@shared/store/reducers/particle.reduer';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { FieldDataTypeModel } from '@shared/models/field-data-type.model';
import { SnackBarService } from '@core/services/snack-bar.service';
import { CommandService } from '@core/services/command.service';
import { AddSurveySectionCommand, AddFieldQuestionCommand } from '../../commands/survey.commands';
import { DataSurveyState } from '../../store/reducers/data-survey.reducers';
import { GetSurveyAction } from '../../store/actions/data-survey.actions';
import { getCurrentSurvey } from '../../store/selectors/data-survey.selectors';
import { Guid } from 'guid-typescript';
import { Configuration } from '@shared/models/configuration';
import { RootDynamicComponentHelper } from '@shared/helpers/root-dynamic-component-helper';
import { MatOptionSelectionChange } from '@angular/material/core';
import {FieldDefinition} from '@shared/models/field-definition';


@Component({
    standalone: false,
    selector: 'particle-add-question-dialog',
  templateUrl: './add-question-dialog.component.html',
  styleUrls: ['./add-question-dialog.component.scss']
})
export class AddQuestionDialogComponent implements OnInit, IDialogView, OnDestroy {
  data: DialogModel;
  addQuestionForm: any;
  allDataTypes: any[] = [];
  sections: any[];
  selectedDataType: any;
  fieldSettings: any;
  unsubscribe$ = new Subject<void>();
  addNewSectionInProgress: boolean;
  survey: any;
  lastAddSectionHeader: string;
  optionName: {};
  isOptionEditing: boolean;
  editingOption: any;
  exitingText: any;
  isQuestionSettingsChecked: boolean;
  selectedSection: any;
  @ViewChild('dynamicComponent', {read: ViewContainerRef}) viewContainerRef;

  constructor(private formBuilder: FormBuilder,
    private particleStore: Store<ParticleState>,
    private commandService: CommandService,
    private dataSurveyStore: Store<DataSurveyState>,
    private componentFactoryResolver: ComponentFactoryResolver,
    private changeDetector: ChangeDetectorRef,
    private snackBarService: SnackBarService) {
    this.addNewSectionInProgress = false;
    this.isOptionEditing = false;
    this.addQuestionForm = this.formBuilder.group({
      questionHeader: new FormControl('', Validators.required),
      questionInstruction: new FormControl(''),
      questionDataType: new FormControl('', Validators.required),
      questionSection: new FormControl('', Validators.required),
      isRequired: new FormControl(true, Validators.required)
    });
    this.isQuestionSettingsChecked = false;
  }

  ngOnInit() {
    this.selectedSection = this.data.input.selectedSection;
    this.invokeSmartGridFieldDataTypes();
    this.dataSurveyStore.pipe(takeUntil(this.unsubscribe$), select(getCurrentSurvey)).subscribe(currentSurvey => {
      this.survey = currentSurvey;
      if (this.survey && this.survey.definitionSections) {
          this.sections = this.survey.definitionSections;
          if (this.lastAddSectionHeader) {
            const selectedSection = this.sections.find(o => o.header === this.lastAddSectionHeader);
            if (selectedSection) {
              this.addQuestionForm.patchValue({['questionSection']: selectedSection.sectionId});
              this.selectedSection = selectedSection;
            }
          }
          if (this.sections.length && this.addQuestionForm.controls['questionSection'].value === '') {
            this.addQuestionForm.patchValue({['questionSection']: this.selectedSection ? this.selectedSection.sectionId : this.sections[0].sectionId});
            if (this.addQuestionForm.controls['questionSection'].value === this.sections[0].sectionId) {
              this.selectedSection = this.sections[0];
            }
          }
      }
    });
    this.particleStore.pipe(select(getSmartGridFieldDataTypes, 'Data-Survey'), (takeUntil(this.unsubscribe$))).subscribe((dataTypes: any[]) => {
      console.log('field data types:', dataTypes);
      this.allDataTypes = dataTypes;
      if (this.allDataTypes.length > 0) {
        this.allDataTypes.forEach(data => {
          let configKey = data.configKey;
          if (configKey === undefined || configKey === null) {
            configKey = '';
          }
          let dataType = data.dataType;
          if (dataType === undefined || dataType === null) {
            dataType = '';
          }
          const uniqueIdentifier = `${dataType},${configKey}`;
          data.dataTypeConfigKey = uniqueIdentifier;
        });
        this.onQuestionTypeChange(this.allDataTypes[0]);
        this.addQuestionForm.patchValue({['questionDataType']: this.selectedDataType.dataTypeConfigKey});
      }
    });
  }
  showAddSection() {
    this.addNewSectionInProgress = true;
  }
  addSection($event) {
    const command = new AddSurveySectionCommand(this.data.input.survey.id, $event.text);
    this.commandService.executeCommand(command)
        .pipe(takeUntil(this.unsubscribe$)).subscribe(commandState => {
          this.lastAddSectionHeader = $event.text;
          this.getCurrentSurvey();
          this.addNewSectionInProgress = false;
        });
  }
  getCurrentSurvey() {
    this.dataSurveyStore.dispatch(new GetSurveyAction(this.data.input.survey.id));
  }

  resetEditing() {
    this.addNewSectionInProgress = false;
  }

  invokeSmartGridFieldDataTypes() {
    this.particleStore.dispatch(new GetSmartGridFieldDataTypesAction('Data-Survey'));
  }

  navigateToQuestionSettings() {
    if(this.fieldSettings){
      this.isQuestionSettingsChecked = true;
      this.generateSettingsEditorComponent();
    }
  }
  generateSettingsEditorComponent() {
    this.changeDetector.detectChanges();
    const resolvedComponent = <Type<any>>RootDynamicComponentHelper.getComponent(this.fieldSettings);
    this.viewContainerRef.clear();
    const component = this.viewContainerRef.createComponent(resolvedComponent);
    component.instance.fieldDefinition = this.selectedDataType;
  }

  onCloseQuestionSettings() {
    this.isQuestionSettingsChecked = false;
  }

  onSelectionChange($event: MatOptionSelectionChange, dataType: any) {
    if ($event.source.selected) {
      this.selectedDataType = JSON.parse(JSON.stringify(dataType));
      this.onQuestionTypeChange(this.selectedDataType);
    }
  }

  onQuestionTypeChange(event: any) {
    this.selectedDataType = event;
    this.fieldSettings = null;
    if(this.selectedDataType.configKey){
      const fieldConfig= Configuration.getSmartGridConfig(this.selectedDataType.configKey);
      if(fieldConfig && fieldConfig.FieldSettings){
        this.fieldSettings = fieldConfig.FieldSettings;
      }
    }
    this.selectedDataType.options.forEach(o => o['isEditing'] = false);
  }
  onSectionChange($event: any) {
    console.log('changed type:', $event);
  }

  onOkClicked(): boolean {
    const header = this.addQuestionForm.controls['questionHeader'].value;
    const title = this.addQuestionForm.controls['questionInstruction'].value;
    const isRequired = this.addQuestionForm.controls['isRequired'].value;
    let dataType = this.addQuestionForm.controls['questionDataType'].value;
    if (dataType && dataType.split(',').length > 0) {
      dataType = dataType.split(',')[0];
    }
    const sectionId = this.addQuestionForm.controls['questionSection'].value;
    if (!this.addQuestionForm.valid) {
      this.snackBarService.showError('Please fill up necessary fields.');
      return false;
    }
    if (this.addQuestionForm.controls['questionHeader'].value.trim() === '') {
      this.snackBarService.showError('Question header cannot be empty.');
      return false;
    }
    const command = new AddFieldQuestionCommand(this.survey.id,
      sectionId,
      header,
      title,
      dataType,
      isRequired,
      this.selectedDataType.options);
    this.data.input.command = command;
    return true;
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  removeCheckItem() {
  }
  moveCheckItem() {
  }
}
